import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { Moon, Sun, Menu, Globe, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n } from "@/i18n/I18nContext";
import { useQuote } from "@/context/QuoteContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { key: "products", href: "#products" },
  { key: "about", href: "#about" },
  { key: "quality", href: "#about" },
  { key: "contact", href: "#contact" },
];

export default function Navbar() {
  const { t, locale, setLocale, locales } = useI18n();
  const { theme, setTheme } = useTheme();
  const { openQuote } = useQuote();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (href) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
        scrolled
          ? "border-b border-border bg-background/80 py-7 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent py-10"
      }`}
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-[1fr_auto_1fr] items-center gap-4 px-5 md:px-10">
        <div className="flex shrink-0 items-center">
          <Logo onClick={() => navigate("/")} />
        </div>

        <nav className="hidden items-center gap-10 lg:flex">
          {NAV.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => goSection(item.href)}
              className="group relative text-2xl font-medium text-foreground/80 transition-colors hover:text-foreground"
            >
              {t(`nav.${item.key}`)}
              <span className="absolute -bottom-1 start-0 h-px w-0 bg-primary transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-1.5 md:gap-2.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="language-trigger"
                className="flex h-12 items-center gap-1.5 border border-border px-4 text-base font-semibold uppercase transition-colors hover:bg-secondary"
              >
                <Globe className="h-4 w-4" />
                {locales[locale].flag}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              {Object.entries(locales).map(([code, meta]) => (
                <DropdownMenuItem
                  key={code}
                  data-testid={`lang-${code}`}
                  onClick={() => setLocale(code)}
                  className={`cursor-pointer text-sm ${locale === code ? "font-bold text-primary" : ""}`}
                >
                  <span className="me-2 text-xs font-semibold text-muted-foreground">{meta.flag}</span>
                  {meta.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex h-12 w-12 items-center justify-center border border-border transition-colors hover:bg-secondary"
          >
            <Sun className="h-4 w-4 dark:hidden" />
            <Moon className="hidden h-4 w-4 dark:block" />
          </button>

          <button
            data-testid="nav-quote-btn"
            onClick={() => openQuote(null)}
            className="hidden items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-lg font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 md:flex"
          >
            {t("nav.quote")}
            <ArrowUpRight className="h-4 w-4" />
          </button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="flex h-12 w-12 items-center justify-center border border-border lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <div className="mt-10 flex flex-col gap-6">
                {NAV.map((item) => (
                  <button
                    key={item.key}
                    data-testid={`mobile-nav-${item.key}`}
                    onClick={() => goSection(item.href)}
                    className="text-start font-display text-2xl font-semibold tracking-tight"
                  >
                    {t(`nav.${item.key}`)}
                  </button>
                ))}
                <button
                  data-testid="mobile-quote-btn"
                  onClick={() => { setMobileOpen(false); openQuote(null); }}
                  className="mt-4 flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
                >
                  {t("nav.quote")} <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}